/**
 * Oprappo design tokens — transcribed from docs/02-tokens.md.
 *
 * This file is exhaustive. If a colour, size or word is not here, it does not exist
 * in Oprappo. Do not add a value without a corresponding line in docs/02-tokens.md.
 */

export const color = {
  teal: '#0E3A45', // primary; founder-mode ground, buttons, headings
  tealDark: '#0A2E37', // founder mode ground, voice mode
  link: '#12626F', // links, secondary actions
  amber: '#E8A33D', // accent — one element per screen, no more
  amberText: '#8A5A0C', // amber that must be readable (eyebrows, labels)
  sand: '#F4F0E7', // surface
  sandDeep: '#EDEAE1', // canvas / recessed
  sandFaint: '#F9F7F2', // inner panels
  border: '#E2DED5', // default border
  borderWarm: '#C4BFB2', // dashed placeholder borders
  borderCool: '#C4D8D5', // teal-tinted borders
  ink: '#16211F', // headings
  ink2: '#1A2422', // body strong
  body: '#3A4846', // body
  muted: '#5B6B69', // captions, secondary — 4.65:1 on sandDeep
  greyBorder: '#8B9795', // BORDERS AND SVG STROKES ONLY — fails contrast as text
  success: '#1E6B4A', // verified, positive
  successOn: '#7FC9A8', // positive on dark ground
  danger: '#B3271E', // errors, blockers
  dangerOn: '#FF9B8F', // errors on dark ground
  onTealMuted: '#8FB0AE', // secondary text on teal
  onTealBody: '#C6D6D4', // body text on teal
  amberInk: '#2D1F02', // text on amber fills

  // ---------------------------------------------------------------------------
  // Added after the build, from colours the DESIGN uses that docs/02 did not list.
  // docs/02 called its palette exhaustive and was wrong about four values; each of
  // these appears across several drawn frames, which is what separates a missing
  // token from a stray value. Every one was MEASURED against the worst surface it
  // actually sits on before being admitted, and the figure is recorded here so the
  // next reader does not have to measure it again.
  //
  // Two colours were REFUSED on the same evidence and corrected in code instead:
  // #C8C2B4 as a day number (1.48:1) and #8B9795 as text (2.51:1), both worse than
  // the value docs/02 already bans outright. See docs/HANDOFF-LOG.md § verify.
  // ---------------------------------------------------------------------------
  warningSurface: '#FDF6E8', // warning panel fill — G2, S6, S7, S9, V2, V3, NT1
  warningBorder: '#B07A16', // that panel's rule. 3.09:1 — a BORDER, and the 4.5:1
  //                           floor is a rule about text, not about lines. Never
  //                           use it as a text colour.
  dangerSurface: '#FAF2F1', // error field fill — Input, Alert, FailureState.Money
  dangerInk: '#8E2018', // error text. 7.37:1 on its own surface — safe.

  // ---------------------------------------------------------------------------
  // ONE COLOUR THAT WAS DRAWN BELOW THE FLOOR, AND WHAT WAS DONE ABOUT IT.
  //
  // `Oprappo P2 Projects and Explore.dc.html` drew PR1's decision-log actor lines —
  // the "who and when" of a decision record — in #5B8F8C, three times and nowhere else
  // in the design set. MEASURED: 3.94:1 on tealDark, 3.36:1 on teal. Both fail the
  // 4.5:1 floor CLAUDE.md states for all readable text, and this IS readable text.
  //
  // The palette already held the colour for exactly that role: `onTealMuted` (#8FB0AE),
  // "secondary text on teal", 6.16:1 on the same ground. So the drawn value read as a
  // slip rather than a decision — but it is still a design decision to say so, and
  // docs/01 § "when the design looks wrong" does not let an agent make one. It was
  // therefore built as drawn and carried to the founder as an open question.
  //
  // RESOLVED: the founder delegated the decision. The actor line is `onTealMuted`, and
  // the change was made in all three places that have to agree — this file, the route
  // (`apps/web/src/routes/projects/PR1Board.tsx`), and the design file itself — so the
  // visual gate compares like with like instead of scoring a difference nobody wants
  // reverted. `belowTextFloor` below is now empty, which is where it should have been
  // all along. See docs/HANDOFF-LOG.md § colour, docs/MEGA-CHECKLIST.md § 1.
  // ---------------------------------------------------------------------------
} as const;

export type ColorToken = keyof typeof color;

/**
 * Colours that must never be used as a text colour. `greyBorder` fails 4.5:1
 * against sand; using it as text is a bug, not a style choice (docs/02 § colour).
 */
export const borderOnlyColors: readonly ColorToken[] = ['greyBorder'];

/**
 * Colours the design draws AS TEXT that do not clear the 4.5:1 floor.
 *
 * This list is empty, and the type is kept so it stays possible to say "this one is
 * drawn below the floor and is still being rendered" without inventing a new mechanism
 * for it. An entry here is an open question rather than a permission: it means a value
 * is being rendered because the design draws it, not because the measurement passed.
 *
 * Three colours have been through this list and none of them survived in it:
 *
 * - #C8C2B4 as a day number (1.48:1) and #8B9795 as text (2.51:1) were REFUSED
 *   outright and corrected in code — both were worse than the value docs/02 bans, and
 *   both had an unambiguous replacement already in use on the same surface.
 * - #5B8F8C on PR1's decision-log actor line (3.94:1) was a marginal miss on a colour
 *   with no other appearance anywhere, so it was rendered as drawn and carried to the
 *   founder. The founder delegated the decision; it is now `onTealMuted` (6.16:1) in
 *   the token file, the route AND the design file. See the comment above `onTealMuted`.
 *
 * An empty list is the goal state, not a reason to stop measuring.
 */
export const belowTextFloor: ReadonlyArray<{
  token: ColorToken;
  on: ColorToken;
  measured: number;
  wouldBe: ColorToken;
  where: string;
}> = [];

export const font = {
  bangla: "'Anek Bangla', sans-serif", // all Bangla UI and body
  display: "'Tiro Bangla', serif", // Bangla display — tagline only
  latin: 'Manrope, sans-serif', // Latin text AND ALL NUMERALS
  serif: "'Instrument Serif', serif", // English display
} as const;

export type FontToken = keyof typeof font;

/** Font families capable of rendering Bangla. The type floor applies to these only. */
export const banglaFamilies = ['Anek Bangla', 'Tiro Bangla'] as const;

/**
 * Bangla type floor: 13px. No exemptions.
 *
 * Verified at the tightest point: in a 72px tab-bar cell at 360px width,
 * `অ্যাক্টিভিটি` measures 57.8px at 13px/500 — 14px of headroom.
 * Latin-only captions in spec documents may run 11.5–12px; that allowance does
 * not extend to Bangla.
 */
export const BANGLA_MIN_PX = 13;

/** Latin-only captions may go this low in spec documents, never in product UI. */
export const LATIN_MIN_PX = 11.5;

/**
 * Line height is size-banded, and the bands are measured — not guessed.
 *
 * Anek Bangla's range rect is a constant ≈1.96 × font-size (ascent + descent,
 * not visible ink). Below 22px that residue is close enough to real ink that
 * conjuncts and matras touch the line below, so 1.75 clears it and the extra
 * leading costs nothing. At and above 22px the same constant reports ~50px of
 * "glyph" for a line with a matra and no descenders at all; applying 1.75 there
 * splits a title into fragments — a real defect chasing a phantom one.
 *
 * Clearance at display sizes comes from the heading's own margin (≥ 0.35em).
 *
 * This rule cost four wrong fixes to arrive at. Measure the constant, then decide
 * where it is telling you about ink and where it is telling you about font metadata.
 */
export const DISPLAY_BAND_PX = 22;
export const leading = {
  banglaBody: 1.75, // Bangla < 22px
  banglaDisplay: 1.25, // Bangla ≥ 22px
  latinHeading: 1.3, // 1.3–1.45
  latinHeadingMax: 1.45,
  latinBody: 1.6, // 1.6–1.8
  latinBodyMax: 1.8,
} as const;

/** Minimum margin on a Bangla display heading, expressed in em (docs/02). */
export const DISPLAY_HEADING_MIN_MARGIN_EM = 0.35;

/** The Anek Bangla range-rect constant, measured. See docs/02 § line height. */
export const ANEK_RANGE_RECT_CONSTANT = 1.96;

export const shadow = {
  bottombar: '0 -2px 14px rgba(14,58,69,.10)',
  sheet: '0 -2px 14px rgba(14,58,69,.14)',
} as const;

/** Radius is 0 everywhere, except chips/pills and avatars. */
export const radius = {
  none: '0',
  pill: '999px',
  avatar: '50%',
} as const;

/**
 * Terminology — a hard rule, not a preference (docs/02 § terminology).
 * `forbidden` strings must never appear in user-visible copy.
 */
export const terminology: ReadonlyArray<{
  forbidden: string;
  use: string;
  why: string;
}> = [
  { forbidden: 'মালিক', use: 'ফাউন্ডার', why: 'মালিক is used of Allah, not of a person' },
  {
    forbidden: 'ব্যালেন্স',
    use: 'অপ্রাপ্য ক্যাশ',
    why: 'Implies a banking licence Oprappo does not hold',
  },
  { forbidden: 'Owner', use: 'Founder', why: 'Same rule in English copy' },
  { forbidden: 'ওয়ালেট', use: 'টাকা ও পেমেন্ট', why: 'Implies a wallet licence' },
  { forbidden: 'ব্যাংকিং', use: 'টাকা ও পেমেন্ট', why: 'Implies a banking licence' },
  { forbidden: 'ইনকাম', use: 'আয়', why: 'Avoids MLM register' },
];

/**
 * Accessible names for the nine icon-only controls (docs/02 § accessible names).
 * Do not invent aria-label text; use the specified string.
 */
export const ariaLabel = {
  voiceSearch: 'বলে খুঁজুন',
  imageSearch: 'ছবি দিয়ে খুঁজুন',
  /** Bell carries a count: ariaLabel.bell(3) → 'খবর — ৩টি নতুন' */
  bell: (n: number) => `খবর — ${toBanglaDigits(n)}টি নতুন`,
  back: 'পিছনে যান',
  close: 'বন্ধ করুন',
  more: 'আরও কাজ',
  send: 'অনুরোধ পাঠান',
  avatar: 'অ্যাকাউন্ট',
  lock: 'সুরক্ষিত এলাকা',
} as const;

const BANGLA_DIGITS = '০১২৩৪৫৬৭৮৯';

/**
 * Convert Latin digits to Bangla digits.
 *
 * Manrope has no Bangla digits — a string containing ০১২৩৪৫৬৭৮৯ must be set in
 * Anek Bangla or it silently falls back and renders at the wrong size. Use this
 * for counts and ordinals in Bangla copy; keep currency figures (৳ 1,190) in
 * Latin digits with Manrope and `font-variant-numeric: tabular-nums`.
 */
export function toBanglaDigits(n: number | string): string {
  return String(n).replace(/[0-9]/g, (d) => BANGLA_DIGITS.charAt(Number(d)));
}

/** True if the string contains any Bangla digit — such a string needs a Bangla family. */
export function hasBanglaDigits(s: string): boolean {
  return /[০-৯]/.test(s);
}

/** True if the string contains any Bangla letterform. */
export function hasBangla(s: string): boolean {
  return /[ঀ-৿]/.test(s);
}

/**
 * The correct line height for a Bangla string at a given size, per the measured bands.
 */
export function banglaLeadingFor(px: number): number {
  return px >= DISPLAY_BAND_PX ? leading.banglaDisplay : leading.banglaBody;
}
